import { z } from 'zod';
/**
 * Objetivos: a pessoa pede ao Secretário para acompanhar algo por um período
 * ("todas as noites vou te contar como foi o dia por 30 dias", "um plano de
 * estudos de 8 semanas") e, no fim, fechar com uma síntese que vira um
 * documento em Documentos. As categorias são as que a própria pessoa nomeia;
 * o Secretário nunca oferece categorias de saúde nem avalia o conteúdo.
 */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data no formato AAAA-MM-DD');
export const OBJECTIVE_CADENCES = ['diario', 'semanal', 'livre'];
export const objectiveCadenceSchema = z.enum(OBJECTIVE_CADENCES);
export const OBJECTIVE_STATUSES = ['ativo', 'fechado', 'arquivado'];
export const objectiveStatusSchema = z.enum(OBJECTIVE_STATUSES);
export const OBJECTIVE_TITLE_MAX = 120;
export const OBJECTIVE_INTENT_MAX = 600;
export const OBJECTIVE_CATEGORIES_MAX = 8;
export const OBJECTIVE_CATEGORY_MAX = 40;
export const OBJECTIVE_ENTRY_MAX = 2000;
/** Registros por pedido (uma conversa rende poucos; a tela manda um por vez). */
export const OBJECTIVE_ENTRIES_PER_REQUEST = 6;
/** Registros devolvidos no detalhe (os mais novos). */
export const OBJECTIVE_DETAIL_ENTRIES = 400;
export const objectiveEntrySourceSchema = z.enum(['texto', 'voz']);
const titleSchema = z.string().trim().min(1).max(OBJECTIVE_TITLE_MAX);
const intentSchema = z.string().trim().max(OBJECTIVE_INTENT_MAX);
const categorySchema = z.string().trim().min(1).max(OBJECTIVE_CATEGORY_MAX);
const categoriesSchema = z.array(categorySchema).max(OBJECTIVE_CATEGORIES_MAX);
export const objectiveSchema = z.object({
    id: z.uuid(),
    title: titleSchema,
    /** O que a pessoa quer ter no fim (uma leitura para um livro, um plano, uma ordem). */
    intent: intentSchema,
    /** Categorias que a própria pessoa nomeou; nunca sugeridas pelo Secretário. */
    categories: categoriesSchema,
    periodStart: isoDate,
    periodEnd: isoDate.nullable(),
    cadence: objectiveCadenceSchema,
    status: objectiveStatusSchema,
    entriesCount: z.number().int().min(0),
    daysWithEntries: z.number().int().min(0),
    lastEntryAt: z.iso.datetime().nullable(),
    /** Cadência diária, em andamento e ainda sem registro hoje (no fuso da pessoa). */
    dueToday: z.boolean(),
    /** A síntese, quando o objetivo foi fechado (uma entrega em Documentos). */
    summaryDeliveryId: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
    closedAt: z.iso.datetime().nullable(),
});
export const objectiveEntrySchema = z.object({
    id: z.uuid(),
    objectiveId: z.uuid(),
    day: isoDate,
    category: z.string().nullable(),
    text: z.string().min(1).max(OBJECTIVE_ENTRY_MAX),
    source: objectiveEntrySourceSchema,
    createdAt: z.iso.datetime(),
});
/** "Até agora": três frases sobre o andamento, refeitas uma vez por semana a partir do sétimo dia. */
export const objectiveProgressSchema = z.object({
    lines: z.array(z.string().min(1).max(200)).min(1).max(3),
    at: z.iso.datetime(),
});
export const objectiveDetailSchema = z.object({
    objective: objectiveSchema,
    /** Do mais novo para o mais antigo. */
    entries: z.array(objectiveEntrySchema).max(OBJECTIVE_DETAIL_ENTRIES),
    /** Ausente nos apps e servidores antigos; null enquanto não há sete dias. */
    progress: objectiveProgressSchema.nullable().optional(),
});
export const objectiveListSchema = z.object({
    objectives: z.array(objectiveSchema),
});
export const createObjectiveSchema = z.object({
    title: titleSchema,
    intent: intentSchema.optional(),
    categories: categoriesSchema.optional(),
    /** Quantos dias a partir de hoje (hoje conta). Vale `periodEnd`, se os dois vierem. */
    periodDays: z.number().int().min(1).max(365).optional(),
    periodEnd: isoDate.optional(),
    cadence: objectiveCadenceSchema.default('diario'),
});
export const addEntriesSchema = z.object({
    entries: z
        .array(z.object({
        category: z.string().trim().max(OBJECTIVE_CATEGORY_MAX).optional(),
        text: z.string().trim().min(1).max(OBJECTIVE_ENTRY_MAX),
    }))
        .min(1)
        .max(OBJECTIVE_ENTRIES_PER_REQUEST),
    source: objectiveEntrySourceSchema.default('texto'),
    /** Dia do registro; sem ele, hoje no fuso da pessoa. */
    day: isoDate.optional(),
});
/**
 * Mudar um registro pela tela: o texto, a categoria (null tira) ou o dia (não pode ser
 * antes do começo nem depois de hoje). Só o que vier muda.
 */
export const updateEntrySchema = z
    .object({
    text: z.string().trim().min(1).max(OBJECTIVE_ENTRY_MAX).optional(),
    category: z.string().trim().max(OBJECTIVE_CATEGORY_MAX).nullable().optional(),
    day: isoDate.optional(),
})
    .refine((value) => value.text !== undefined || value.category !== undefined || value.day !== undefined, {
    message: 'Nada para mudar.',
});
export const updateObjectiveSchema = z.object({
    status: objectiveStatusSchema.optional(),
    title: titleSchema.optional(),
    intent: intentSchema.optional(),
    categories: categoriesSchema.optional(),
});
/** Resposta do fechamento: o objetivo já fechado e a entrega com a síntese. */
export const closeObjectiveResultSchema = z.object({
    objective: objectiveSchema,
    deliveryId: z.uuid(),
});
